import { emailDrClearCareEstimate, textDrClearCareEstimate } from 'api/Appointment';

import { useDocumentShare } from 'lib/hooks/useDocumentShare';

import Icon from 'components/shared/Icon/Icon';
import Modal from 'components/shared/Modal/Modal';
import Loader from 'components/shared/Loader/Loader';
import Button from 'components/shared/Button/Button';
import DocumentShareActions from 'components/shared/DocumentSheet/components/DocumentShareActions';

import DrClearCare from './DrClearCare';

const DR_CLEARCARE_PDF_WIDTH = 768;

const DrClearCareModal = ({
  estimate,
  isLoading = false,
  isError = false,
  onRetry,
  practice,
  appointmentId,
  isOpen,
  onClose,
  onAddInsurance,
  hasInsurance = null,
  channels
}) => {
  const { contentRef, busy, handleDownload, handleSend } = useDocumentShare({
    filename: 'Dr. Clearcare.pdf',
    noun: 'estimate',
    // The guide's column is max-w-[720px] + px-6; laying the PDF copy out at exactly that width
    // makes it fill the page instead of floating centered in the full-width modal.
    captureWidth: DR_CLEARCARE_PDF_WIDTH,
    sendParams: { appointmentId },
    emailRequest: emailDrClearCareEstimate,
    textRequest: textDrClearCareEstimate
  });

  const hasEstimate = !!estimate;

  return (
    <Modal
      className="xs:!rounded-none"
      isOpen={!!isOpen}
      title="Understanding your visit costs"
      width="full"
      handleClose={onClose}
      bodyClassName="p-0"
      headerClassName="bg-white border-b border-neutral-200 border-solid border-0 px-4 py-3 gap-2"
      headButton={
        hasEstimate && (
          <div className="flex w-full items-center justify-end gap-2 pr-3">
            <DocumentShareActions
              busy={busy}
              onDownload={handleDownload}
              onSend={handleSend}
              canSend={!!appointmentId}
              channels={channels}
            />
          </div>
        )
      }
      isFooter={false}
      overlayClassName="xs:!p-0">
      {isLoading ? (
        <div className="flex h-[60vh] w-full items-center justify-center">
          <Loader color="primary" size={40} />
        </div>
      ) : isError ? (
        <div className="flex h-[60vh] w-full flex-col items-center justify-center gap-4 px-8 text-center">
          <Icon icon="error-triangle" size={64} color="danger" />
          <h3 className="m-0 text-lg font-600 text-neutral-900">We couldn’t load your estimate</h3>
          <p className="m-0 max-w-[420px] text-sm text-neutral-600">
            Something went wrong while loading your visit costs. Please try again.
          </p>
          {onRetry && (
            <Button text="Try again" icon="reset" size="medium" onClick={() => onRetry()} />
          )}
        </div>
      ) : hasEstimate ? (
        <div ref={contentRef}>
          <DrClearCare estimate={estimate} practice={practice} />
        </div>
      ) : (
        <div className="flex h-[60vh] w-full flex-col items-center justify-center gap-4 px-8 text-center">
          <Icon icon="shield-mono" size={64} color="primary" />
          <h3 className="m-0 text-lg font-600 text-neutral-900">No benefits on file yet</h3>
          <p className="m-0 max-w-[420px] text-sm text-neutral-600">
            {hasInsurance === false
              ? 'Add an insurance profile so we can verify your eligibility benefits and show your estimated visit costs here.'
              : 'We’ll show your estimated visit costs here once your benefits have been verified.'}
          </p>
          {onAddInsurance && hasInsurance === false && (
            <Button
              text="Add insurance"
              icon="add-circle-mono"
              size="medium"
              onClick={onAddInsurance}
            />
          )}
        </div>
      )}
    </Modal>
  );
};

export default DrClearCareModal;
